import type { Reporte } from '@barbones/shared';
import { formatearCelda } from './TablaReporte';

interface Barra {
  etiqueta: string;
  valor: number;
}

/**
 * Barras horizontales accesibles (sin librería de gráficas): cada barra es una fila con
 * etiqueta y valor en texto, así el dato no depende solo del largo ni del color.
 */
export function BarrasHorizontales({ datos, tipo = 'dinero', titulo }: { datos: Barra[]; tipo?: 'dinero' | 'numero'; titulo: string }) {
  const max = Math.max(1, ...datos.map((d) => d.valor));
  return (
    <figure className="grid gap-2.5" aria-label={titulo}>
      <figcaption className="sr-only">{titulo}</figcaption>
      {datos.map((d) => (
        <div key={d.etiqueta} className="grid grid-cols-[minmax(84px,160px)_1fr_auto] items-center gap-3 text-[13.5px]">
          <span className="truncate" title={d.etiqueta}>
            {d.etiqueta}
          </span>
          <div className="h-3.5 overflow-hidden rounded-full bg-muted">
            <div
              className="h-full origin-left rounded-full bg-gradient-to-r from-accent/55 to-accent transition-[width] duration-500 ease-out"
              style={{ width: `${Math.max(0, (d.valor / max) * 100)}%` }}
            />
          </div>
          <span className="cifras min-w-20 text-right font-medium">{formatearCelda(tipo, d.valor)}</span>
        </div>
      ))}
    </figure>
  );
}

/** Columnas verticales compactas (p. ej. ingresos de los últimos 7 días). */
export function Columnas({ datos, alto = 120 }: { datos: Barra[]; alto?: number }) {
  const max = Math.max(1, ...datos.map((d) => d.valor));
  return (
    <div className="flex items-end gap-2" style={{ height: alto }} role="list">
      {datos.map((d, i) => (
        <div key={d.etiqueta} role="listitem" className="group flex h-full flex-1 flex-col items-center justify-end gap-1.5" title={`${d.etiqueta}: ${formatearCelda('dinero', d.valor)}`}>
          <span className="cifras text-[11px] text-foreground-muted opacity-0 transition-opacity group-hover:opacity-100">{formatearCelda('dinero', d.valor)}</span>
          <div
            className={`w-full rounded-t-md bg-gradient-to-t ${i === datos.length - 1 ? 'from-accent/70 to-accent' : 'from-foreground/15 to-foreground/30'}`}
            style={{ height: `${Math.max(3, (d.valor / max) * (alto - 36))}px` }}
          />
          <span className="text-[11.5px] capitalize text-foreground-muted">{d.etiqueta}</span>
        </div>
      ))}
    </div>
  );
}

export function GraficaReporte({ reporte }: { reporte: Reporte }) {
  const g = reporte.grafica;
  if (!g) return null;
  const col = reporte.columnas.find((c) => c.clave === g.valor);
  const datos = reporte.filas.map((f) => ({ etiqueta: String(f[g.etiqueta] ?? '—'), valor: Number(f[g.valor] ?? 0) }));
  return <BarrasHorizontales titulo={g.serie} datos={datos} tipo={col?.tipo === 'dinero' ? 'dinero' : 'numero'} />;
}
